import { CARDS } from "../../content/cards.js";
import { LOAD_EFFECTS } from "../../content/loads.js";
import { ORGANIZATIONS } from "../../content/organizations.js";
import { createMapEdit, type MapEdit } from "../stageEditor/mapEdit.js";
import type { StartDeck } from "../stageEditor/startDeck.js";
import type { StoredRewards } from "../stageEditor/storedRewards.js";

export type StageStatus = "draft" | "published";

export type StoredStage = {
  id: string;
  status: StageStatus;
  name: string;
  difficulty: string;
  flavor: string;
  map: MapEdit;
  rewards?: StoredRewards;
  startDeck?: StartDeck;
  updatedAt: number;
};

const NODE_KINDS = new Set(["start", "case", "rest", "event", "relic"]);
const CASE_SIZES = new Set(["small", "medium", "large", "boss"]);
const MAX_NODES = 200;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isText(value: unknown, maximum: number, allowEmpty = true): value is string {
  return typeof value === "string" && value.length <= maximum && (allowEmpty || value.length > 0);
}

function isInteger(value: unknown, minimum: number, maximum: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= minimum && value <= maximum;
}

function isStringList(value: unknown, maximum = 200): value is string[] {
  return Array.isArray(value) && value.length <= maximum
    && value.every((item) => isText(item, 128, false))
    && new Set(value).size === value.length;
}

function isRewards(value: unknown): value is StoredRewards {
  return isRecord(value)
    && isStringList(value.small) && value.small.every((id) => id in CARDS)
    && isStringList(value.medium) && value.medium.every((id) => id in CARDS)
    && isStringList(value.large) && value.large.every((id) => id in CARDS)
    && isStringList(value.orgs) && value.orgs.every((id) => id in ORGANIZATIONS);
}

function isStartDeck(value: unknown): value is StartDeck {
  return isRecord(value)
    && isInteger(value.intern, 0, 20)
    && isInteger(value.idle, 0, 20)
    && isInteger(value.hedge, 0, 20)
    && typeof value.allowBringIn === "boolean";
}

function isMap(value: unknown): value is MapEdit {
  if (!isRecord(value)
    || !isRecord(value.nodes)
    || Object.keys(value.nodes).length > MAX_NODES
    || !(value.selectedId === null || isText(value.selectedId, 64, false))
    || !NODE_KINDS.has(String(value.placeKind))
    || !isInteger(value.nextSeq, 1, 1_000_000)
    || !isInteger(value.cols, 1, 20)
    || !isInteger(value.rows, 1, 200)) return false;

  const ids = new Set(Object.keys(value.nodes));
  const cols = value.cols as number;
  const rows = value.rows as number;
  return Object.entries(value.nodes).every(([key, node]) => {
    if (!isRecord(node)
      || node.id !== key
      || !isText(node.id, 64, false)
      || !NODE_KINDS.has(String(node.kind))
      || !isInteger(node.x, 0, cols - 1)
      || !isInteger(node.y, 0, rows - 1)
      || !Array.isArray(node.next)
      || node.next.length > MAX_NODES
      || !node.next.every((id) => isText(id, 64, false) && ids.has(id))
      || new Set(node.next).size !== node.next.length
      || !CASE_SIZES.has(String(node.size))
      || !isText(node.name, 120)
      || !isInteger(node.required, 0, 10_000)
      || !isInteger(node.deadline, 0, 1_000)
      || !Array.isArray(node.loads)
      || node.loads.length > 100
      || !node.loads.every((load) => isRecord(load)
        && isText(load.id, 128, false)
        && load.id in LOAD_EFFECTS
        && isInteger(load.weight, 0, 1_000_000))
      || !isText(node.flavor, 2_000)) return false;
    return true;
  });
}

export function parseStoredStage(value: unknown): StoredStage | null {
  if (!isRecord(value)
    || !isText(value.id, 64, false)
    || !/^[A-Za-z0-9_-]+$/.test(value.id)
    || (value.status !== "draft" && value.status !== "published")
    || !isText(value.name, 120, false)
    || !isText(value.difficulty, 120)
    || !isText(value.flavor, 2_000)
    || !isMap(value.map)
    || (value.rewards !== undefined && !isRewards(value.rewards))
    || (value.startDeck !== undefined && !isStartDeck(value.startDeck))
    || !isInteger(value.updatedAt, 0, Number.MAX_SAFE_INTEGER)) return null;
  return value as StoredStage;
}

export function createDraft(now: number): StoredStage {
  return {
    id: `custom-${now}`,
    status: "draft",
    name: "新規ステージ",
    difficulty: "下書き",
    flavor: "",
    map: createMapEdit(),
    updatedAt: now,
  };
}
